import UserAccount from './UserAccount.mjs';
import { query, showElement, hideElement } from '../utils.mjs';
import WindCreekClient from "./WindCreekClient.mjs";

UserAccount.windCreekClient = new WindCreekClient();

let winLossStatementPage = query('[data-win-loss-statement-page]');
let statement = winLossStatementPage.querySelector('[data-statement-container]');

let winLossStatementByProperty = {};
let winLossStatementByMonth = [];

let properties = {
  'alabama': 'Alabama',
  'aruba': 'Aruba',
  'bethlehem': 'Bethlehem',
  'chicago': 'Chicago',
  'curacao': 'Curaçao',
  'iGaming PA': 'iGaming PA',
};

let months = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec'
];

// Add up all the values in an object
function netWinLoss(obj) {
  let sum = 0;
  for (let el in obj) {
    if (obj.hasOwnProperty(el)) {
      sum += parseFloat(obj[el]);
    }
  }
  return sum;
}

// Add up all the property's activity for the whole year
function getPropertyTotal(property) {
  let sum = 0;
  winLossStatementByProperty[property].forEach((month) => {
    sum += parseFloat(month.value);
  });
  return sum;
}

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',

  // These options are needed to round to whole numbers if that's what you want.
  //minimumFractionDigits: 0, // (this suffices for whole numbers, but will print 2500.10 as $2,500.1)
  //maximumFractionDigits: 0, // (causes 2500.99 to be printed as $2,501)
});
function formatCurrency(value) {
  if (value < 0) {
    value = value * -1;
    return '(' + currencyFormatter.format(value) + ')';
  } else {
    return currencyFormatter.format(value);
  }
}

// Generate the HTML for the win/loss statement
function buildWinLossStatement(winLossStatement, year) {
  // Organize the data by property
  winLossStatement.forEach(property => {
    winLossStatementByProperty[property.name] = property.propertyValue;
  });

  // Organize the data by month
  for (let i = 0; i <= 11; i++) {
    winLossStatementByMonth[i] = {};
    for (let propertyKey in properties) {
      winLossStatementByMonth[i][propertyKey] = winLossStatementByProperty[propertyKey][i].value;
    }
  }

  statement.innerHTML = '';
  let statementHTML = '';

  winLossStatementByMonth.forEach((monthData, index) => {
    let textColorClasses = {
      dark: 'text-body',
      subtle: 'text-dark-subtle',
      primary: 'text-primary'
    };

    if (netWinLoss(monthData) === 0) {
      // This month has no activity
      statementHTML += `
        <div class="month inactive bg-light ${textColorClasses.subtle} px-3 py-2">
          <div class="d-flex justify-content-between">
            <h3 class="subhead-1 mb-0">${months[index]}</h3>
            <span class="mx-4 fw-normal no-activity">no activity</span>
          </div>
        </div>
        `;
    } else {
      // Start by generating the HTML for all the property columns in this row
      let propertyColumns = '';
      for (let property in monthData) {
        let displayValue = '';
        let noResultClass = '';
        if (monthData[property] === 0) {
          displayValue = `<span class="material-icons mt-lg-1 ${textColorClasses.subtle}">remove</span>`;
        } else {
          displayValue = `<span class="h4 ${textColorClasses.primary} mb-0">${formatCurrency(monthData[property])}</span>`;
        }
        propertyColumns += `
          <div class="col-6 col-sm-4 col-md property-result">
            <div class="d-flex flex-column gap-2 justify-content-center align-items-center py-2">
              <h4 class="property-heading subhead-2 ${textColorClasses.subtle} mb-0">${properties[property]}</h4>
              <hr class="my-0 w-100 ${textColorClasses.dark}">
              ${displayValue}
            </div>
          </div>
        `;
      }

      // Now build the full month row. Inject the property columns (generated above) and add the last column for this month's total.
      statementHTML += `
        <div class="month shadow bg-white active rounded px-3 py-2">
          <div class="row">
            <div class="col-lg-1">
              <div class="d-flex justify-content-center justify-content-lg-between align-items-center h-100">
                <h3 class="${textColorClasses.primary} mb-0 py-3 py-lg-0">${months[index]}</h3>
                <div class="vr d-none d-lg-block"></div>
              </div>
            </div>
            <div class="col-lg d-flex flex-column justify-content-center">
              <div class="row justify-content-center justify-content-md-start">
                ${propertyColumns}
              </div>
            </div>
            <div class="col-12 col-lg-auto">
              <hr class="w-100 d-lg-none mt-2 mb-0 do-not-print">
              <div class="d-flex justify-content-center justify-content-lg-between align-items-center h-100">
                <div class="vr d-none d-lg-block"></div>
                <div class="d-flex flex-column gap-2 justify-content-center align-items-center py-2 ps-lg-4">
                  <h3 class="label-1 mb-0 ${textColorClasses.subtle}">Net Win/Loss</h3>
                  <hr class="my-0 w-100">
                  <span class="h3 ${textColorClasses.primary} mb-0">
                    ${formatCurrency(netWinLoss(monthData))}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      `;
    }
  });

  // Total row
  let textColorClasses = {
    dark: 'text-white',
    subtle: 'text-white',
    primary: 'text-white'
  };

  let totals = {};
  let grandTotal = 0;
  for (let property in properties) {
    totals[property] =  getPropertyTotal(property);
    grandTotal += totals[property];
  }

  // Start by generating the HTML for all the property columns in this row
  let propertyColumns = '';
  for (let property in totals) {
    let displayValue = '';
    if (totals[property] === 0) {
      displayValue = `<span class="material-icons mt-lg-1 ${textColorClasses.subtle}">remove</span>`;
    } else {
      displayValue = `<span class="h4 ${textColorClasses.primary} mb-0">${formatCurrency(totals[property])}</span>`;
    }
    propertyColumns += `
      <div class="col-6 col-sm-4 col-md property-result">
        <div class="d-flex flex-column gap-2 justify-content-center align-items-center py-2">
          <h4 class="property-heading subhead-2 ${textColorClasses.subtle} mb-0">${properties[property]}</h4>
          <hr class="my-0 w-100 ${textColorClasses.dark}">
          ${displayValue}
        </div>
      </div>
    `;
  }

  // Now build the full total row. Inject the property columns (generated above) and add the last column for the grand total.
  statementHTML += `
    <div class="net bg-primary  active rounded px-3 py-2">
      <div class="row">
        <div class="col-lg-1">
          <div class="d-flex justify-content-center justify-content-lg-between align-items-center h-100">
            <h3 class="${textColorClasses.primary} mb-0 py-3 py-lg-0">Totals</h3>
            <div class="vr d-none d-lg-block"></div>
          </div>
        </div>
        <div class="col-lg d-flex flex-column justify-content-center">
          <div class="row justify-content-center justify-content-md-start">
            ${propertyColumns}
          </div>
        </div>
        <div class="col-12 col-lg-auto">
          <hr class="w-100 d-lg-none mt-2 mb-0 do-not-print">
          <div class="d-flex justify-content-center justify-content-lg-between align-items-center h-100">
            <div class="vr d-none d-lg-block"></div>
            <div class="d-flex flex-column gap-2 justify-content-center align-items-center py-2 ps-lg-4">
              <h3 class="label-1 mb-0 ${textColorClasses.subtle}">Net Win/Loss</h3>
              <hr class="my-0 w-100">
              <span class="h3 ${textColorClasses.primary} mb-0">
                ${formatCurrency(grandTotal)}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  `;

  statement.innerHTML = statementHTML;
}

// On page load, populate the table with the data from the current year
if (winLossStatementPage && !UserAccount.isMiamiSite()) {
  if (UserAccount.isLoggedIn()) {
    // The user has an existing session. Call the API to retrieve the win/loss statement for the current year.
    let currentDate = new Date();
    UserAccount.getWinLossStatement(currentDate.getFullYear()).then(winLossStatement => {
      buildWinLossStatement(winLossStatement, currentDate.getFullYear());
      let formattedDate = currentDate.getMonth() + 1 + '/' + currentDate.getDate() + '/' + currentDate.getFullYear();
      winLossStatementPage.querySelector('[data-report-generated-date]').textContent = formattedDate;
      winLossStatementPage.classList.remove('loading-animation');
    })
    .catch(error => {
      showElement('[data-page-error-message]');
      hideElement('[data-hide-on-page-error]');
      winLossStatementPage.classList.remove('loading-animation');
      console.log('unable to load win loss statement');
      console.error(error);
      newrelic.noticeError(error);
    });

    // On page load, dynamically build the list of year options, starting with the current year and working our way back to the year they joined
    UserAccount.getProfile().then(profileData => {
      let joinYear = parseInt(profileData.patronInfo.rewardsJoinDate.split('/')[2]);
      let currentYear = currentDate.getFullYear();
      let yearOptionsHTML = '';
      for (let i = currentYear; i >= joinYear; i--) {
        let selected = (i === currentYear) ? 'selected' : '';
        yearOptionsHTML += `<option value="${i}" ${selected}>${i}</option>`;
      }
      winLossStatementPage.querySelector('[data-select-year]').innerHTML = yearOptionsHTML;
      winLossStatementPage.querySelector('[data-select-year-container]').classList.remove('loading-animation');
    }).catch(error => {
      hideElement('[data-select-year-container]');
      console.log('unable to build year options');
      console.error(error);
      newrelic.noticeError(error);
    });

    // If the user changes the year, get the data for that year and rebuild the table
    let yearSelect = winLossStatementPage.querySelector('[data-select-year]');
    yearSelect.addEventListener('change', event => {
      if (yearSelect.value !== '') {
        statement.classList.add('loading-animation');
        UserAccount.getWinLossStatement(yearSelect.value).then(winLossStatement => {
            buildWinLossStatement(winLossStatement, yearSelect.value);
            hideElement('[data-year-error-message]');
            showElement('[data-download-pdf]');
            statement.classList.remove('loading-animation');
        })
        .catch(error => {
          statement.innerHTML = '';
          showElement('[data-year-error-message]');
          hideElement('[data-download-pdf]');
          statement.classList.remove('loading-animation');
          console.log('unable to load win loss statement for year ' + yearSelect.value);
          console.error(error);
          newrelic.noticeError(error);
        });
      }
    });

    // Handle the click event for the PDF download button
    winLossStatementPage.querySelector('[data-download-pdf]').addEventListener('click', event => {
      const select = document.querySelector('#win-loss-year');
      const selectedOption = select.options[select.selectedIndex];
      document.getElementById('print-year').innerHTML = selectedOption.text;

      // Specific to the heading printing weirdly on Firefox for Mac
      const heading = document.querySelector('.wc-hero h2.h1');
      const isMac = navigator.platform.includes("Mac");
      const isFirefox = navigator.userAgent.includes("Firefox");

      if (heading && isMac && isFirefox) {
        heading.classList.add("mac-firefox-font-workaround");
      }

      window.print();
    });
  } else {
    // User is not logged in. Redirect to home page and prompt for authentication.
    UserAccount.forceAuthentication();
  }
}
